import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {
    Avatar,
    Badge,
    Box,
    CircularProgress,
    Fab,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import ChatRoundedIcon from "@mui/icons-material/ChatRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import {useSelector} from "react-redux";
import {APP_NAME, useWebSocket} from "../websocket";
import {ws_getMessages, ws_markRead} from "../../network/api/ws.api";
import {media_AddImage} from "../../network/api/multimedia.api";
import {ErrorContext} from "../../components/GympinPagesProvider";
import {replacePersianNumbers} from "../utils";

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

function shouldShowMessage(msg, myApp) {
    return (msg?.AppName ?? "CLIENT") === myApp;
}

function messageTime(msg) {
    const raw = msg?.CreatedDate || msg?.CreationDate || msg?.Date;
    if (!raw) return "";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return "";
    return replacePersianNumbers(
        d.toLocaleTimeString("fa-IR", {hour: "2-digit", minute: "2-digit"})
    );
}

function imageUrl(msg) {
    return msg?.Multimedia?.Url || msg?.MultimediaUrl || null;
}

const ChatWidget = () => {
    const error = useContext(ErrorContext);
    const currentUser = useSelector((state) => state.auth.user);
    const {connected, status, sendChatMessage, addChatListener, myUserId} = useWebSocket();
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [unread, setUnread] = useState(0);
    const listRef = useRef(null);
    const fileRef = useRef(null);
    const openRef = useRef(false);

    useEffect(() => {
        openRef.current = open;
    }, [open]);

    const scrollToEnd = useCallback(() => {
        requestAnimationFrame(() => {
            if (listRef.current) {
                listRef.current.scrollTop = listRef.current.scrollHeight;
            }
        });
    }, []);

    const appendMessage = useCallback((msg) => {
        if (!shouldShowMessage(msg, APP_NAME)) return;
        setMessages((prev) => {
            const id = msg?.Id;
            if (id != null && prev.some((m) => m?.Id === id)) return prev;
            return [...prev, msg];
        });
        if (msg?.Sender === "ADMIN") {
            setOpen(true);
            openRef.current = true;
            setUnread(0);
            if (myUserId) {
                ws_markRead(myUserId, APP_NAME).catch(() => {});
            }
        }
        scrollToEnd();
    }, [myUserId, scrollToEnd]);

    const loadHistory = useCallback(() => {
        if (!myUserId) return;
        setLoading(true);
        ws_getMessages(myUserId, APP_NAME)
            .then((result) => {
                const data = result?.data?.Data;
                const list = Array.isArray(data) ? data : (data?.content || data?.Content || []);
                setMessages(list.filter((m) => shouldShowMessage(m, APP_NAME)));
                scrollToEnd();
            })
            .catch((e) => {
                error.showError({message: e?.response?.data?.Message || "خطا در دریافت پیام‌ها"});
            })
            .finally(() => setLoading(false));
    }, [error, myUserId, scrollToEnd]);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    useEffect(() => {
        const unsubscribe = addChatListener((msg) => appendMessage(msg));
        return unsubscribe;
    }, [addChatListener, appendMessage]);

    const handleOpen = () => {
        setOpen(true);
        setUnread(0);
        if (myUserId) {
            ws_markRead(myUserId, APP_NAME).catch(() => {});
        }
        scrollToEnd();
    };

    const handleSendText = () => {
        const text = (draft || "").trim();
        if (!text || sending) return;
        setSending(true);
        sendChatMessage({Message: text, Sender: "USER"}, (ok) => {
            setSending(false);
            if (ok) {
                setDraft("");
                appendMessage({
                    Message: text,
                    Sender: "USER",
                    AppName: APP_NAME,
                    CreatedDate: new Date().toISOString()
                });
            } else {
                error.showError({message: "ارسال پیام ممکن نشد. اتصال را بررسی کنید."});
            }
        });
    };

    const handlePickImage = (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        const type = (file.type || "").toLowerCase();
        if (type !== "image/png" && type !== "image/jpeg" && type !== "image/jpg") {
            error.showError({message: "فقط تصویر PNG یا JPG مجاز است."});
            return;
        }
        if (file.size > MAX_IMAGE_BYTES) {
            error.showError({message: "حجم تصویر حداکثر ۵ مگابایت است."});
            return;
        }
        const formData = new FormData();
        formData.append("MediaType", "IMAGE");
        formData.append("File", file);
        formData.append("CategoryId", "2");
        formData.append("Title", currentUser?.Username || "chat");
        formData.append("Description", String(currentUser?.Id || ""));
        setSending(true);
        media_AddImage(formData)
            .then((res) => {
                const multimediaId = res?.data?.Data?.Id;
                const url = res?.data?.Data?.Url;
                if (!multimediaId) throw new Error("no-id");
                sendChatMessage({
                    Message: "",
                    Sender: "USER",
                    MultimediaId: multimediaId
                }, (ok) => {
                    setSending(false);
                    if (ok) {
                        appendMessage({
                            Message: "",
                            Sender: "USER",
                            AppName: APP_NAME,
                            Multimedia: {Url: url},
                            MultimediaId: multimediaId,
                            CreatedDate: new Date().toISOString()
                        });
                    } else {
                        error.showError({message: "ارسال تصویر ممکن نشد."});
                    }
                });
            })
            .catch((e) => {
                setSending(false);
                error.showError({message: e?.response?.data?.Message || "خطا در آپلود تصویر"});
            });
    };

    const isLinked = connected === true || status === "CONNECTED";
    const visibleMessages = useMemo(
        () => messages.filter((m) => shouldShowMessage(m, APP_NAME)),
        [messages]
    );

    return (
        <>
            {!open && (
                <Fab
                    color="primary"
                    aria-label="چت پشتیبانی"
                    onClick={handleOpen}
                    sx={{
                        position: "fixed",
                        bottom: {xs: 84, sm: 88},
                        left: 16,
                        zIndex: 1099,
                        boxShadow: "0 6px 18px rgba(231, 51, 62, 0.4)"
                    }}
                >
                    <Badge badgeContent={unread} color="error" overlap="circular">
                        <ChatRoundedIcon />
                    </Badge>
                </Fab>
            )}

            {open && (
                <Paper
                    elevation={16}
                    sx={{
                        position: "fixed",
                        bottom: {xs: 84, sm: 88},
                        left: 12,
                        right: {xs: 12, sm: "auto"},
                        width: {xs: "auto", sm: 360},
                        maxWidth: "calc(100vw - 24px)",
                        height: {xs: "52vh", sm: 460},
                        zIndex: 1099,
                        borderRadius: 4,
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        bgcolor: "background.paper",
                        border: "1px solid",
                        borderColor: "divider"
                    }}
                >
                    <Box
                        sx={{
                            px: 1.5,
                            py: 1.2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            background: "linear-gradient(135deg, #e7333e 0%, #b22831 100%)",
                            color: "#fff"
                        }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <SupportAgentRoundedIcon />
                            <Box>
                                <Typography sx={{fontWeight: 800, fontSize: "0.95rem"}}>
                                    پشتیبانی جیم‌پین
                                </Typography>
                                <Typography sx={{fontSize: "0.7rem", opacity: 0.9}}>
                                    {isLinked ? "متصل" : (status === "INACTIVE" ? "قطع شده" : "در حال اتصال...")}
                                </Typography>
                            </Box>
                        </Stack>
                        <IconButton size="small" onClick={() => setOpen(false)} sx={{color: "#fff"}}>
                            <CloseRoundedIcon />
                        </IconButton>
                    </Box>

                    <Box
                        ref={listRef}
                        sx={{
                            flex: 1,
                            overflowY: "auto",
                            px: 1.4,
                            py: 1.2,
                            bgcolor: (theme) => theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "#f7f7f7"
                        }}
                    >
                        {loading && (
                            <Box sx={{display: "flex", justifyContent: "center", py: 3}}>
                                <CircularProgress size={26} />
                            </Box>
                        )}
                        {!loading && visibleMessages.length < 1 && (
                            <Typography variant="body2" sx={{color: "text.secondary", textAlign: "center", mt: 4}}>
                                پیامی نیست. سوال خود را بنویسید.
                            </Typography>
                        )}
                        <Stack spacing={1}>
                            {visibleMessages.map((msg, index) => {
                                const mine = msg?.Sender === "USER";
                                const img = imageUrl(msg);
                                return (
                                    <Box
                                        key={msg?.Id ?? `m-${index}`}
                                        sx={{
                                            display: "flex",
                                            justifyContent: mine ? "flex-end" : "flex-start",
                                            alignItems: "flex-end",
                                            gap: 0.7
                                        }}
                                    >
                                        {!mine && (
                                            <Avatar sx={{width: 26, height: 26, bgcolor: "primary.main"}}>
                                                <SupportAgentRoundedIcon sx={{fontSize: 16}} />
                                            </Avatar>
                                        )}
                                        <Box
                                            sx={{
                                                maxWidth: "78%",
                                                px: 1.3,
                                                py: 0.9,
                                                borderRadius: mine ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                                                bgcolor: mine ? "primary.main" : "background.paper",
                                                color: mine ? "#fff" : "text.primary",
                                                boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
                                            }}
                                        >
                                            {img && (
                                                <Box
                                                    component="img"
                                                    src={img}
                                                    alt="تصویر چت"
                                                    sx={{
                                                        display: "block",
                                                        maxWidth: "100%",
                                                        borderRadius: 1.5,
                                                        mb: msg?.Message ? 0.8 : 0
                                                    }}
                                                />
                                            )}
                                            {msg?.Message ? (
                                                <Typography sx={{fontSize: "0.88rem", whiteSpace: "pre-wrap", lineHeight: 1.7}}>
                                                    {msg.Message}
                                                </Typography>
                                            ) : null}
                                            <Typography sx={{fontSize: "0.65rem", opacity: 0.75, mt: 0.4, textAlign: "left"}}>
                                                {messageTime(msg)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Box>

                    <Box sx={{p: 1, borderTop: "1px solid", borderTopColor: "divider", display: "flex", gap: 0.5}}>
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/png,image/jpeg"
                            hidden
                            onChange={handlePickImage}
                        />
                        <IconButton
                            disabled={sending}
                            onClick={() => fileRef.current?.click()}
                            sx={{color: "text.secondary"}}
                        >
                            <ImageOutlinedIcon />
                        </IconButton>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="پیام خود را بنویسید..."
                            value={draft}
                            disabled={sending}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendText();
                                }
                            }}
                            sx={{"& .MuiOutlinedInput-root": {borderRadius: 3}}}
                        />
                        <IconButton
                            color="primary"
                            disabled={sending || !(draft || "").trim()}
                            onClick={handleSendText}
                        >
                            {sending ? <CircularProgress size={18} /> : <SendRoundedIcon />}
                        </IconButton>
                    </Box>
                </Paper>
            )}
        </>
    );
};

export default ChatWidget;
